import {
  AccessTime as AccessTimeIcon,
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  Notes as NotesIcon,
  Payment as PaymentIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Client } from '../services/clientApi';
import { colors } from '../theme';
import CancelButton from './CancelButton';

interface AppointmentModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (appointmentData: AppointmentData) => void;
  clients: Client[];
  dietitianId?: string;
  editingAppointment?: AppointmentData | null;
}

interface AppointmentData {
  id?: string;
  dietitianId: string;
  clientId: string;
  clientName: string;
  appointmentDate: string;
  startTime: string;
  endTime: string;
  type: string;
  status: string;
  notes: string;
  location: string;
  fee: number;
  isPaid: boolean;
}

const AppointmentModal: React.FC<AppointmentModalProps> = ({
  open,
  onClose,
  onSave,
  clients,
  dietitianId = '',
  editingAppointment,
}) => {
  const [formData, setFormData] = useState<AppointmentData>({
    dietitianId: dietitianId,
    clientId: '',
    clientName: '',
    appointmentDate: '',
    startTime: '',
    endTime: '',
    type: '',
    status: 'Planlandı',
    notes: '',
    location: '',
    fee: 0,
    isPaid: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (editingAppointment) {
      setFormData(editingAppointment);
    } else {
      setFormData({
        dietitianId: dietitianId,
        clientId: '',
        clientName: '',
        appointmentDate: '',
        startTime: '',
        endTime: '',
        type: '',
        status: 'Planlandı',
        notes: '',
        location: '',
        fee: 0,
        isPaid: false,
      });
    }
  }, [editingAppointment, open, dietitianId]);

  const handleInputChange =
    (field: keyof AppointmentData) =>
    (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any
    ) => {
      const value =
        event.target.type === 'checkbox'
          ? event.target.checked
          : field === 'fee'
          ? parseFloat(event.target.value) || 0
          : event.target.value;

      setFormData((prev) => ({ ...prev, [field]: value }));

      if (field === 'clientId') {
        const selectedClient = clients.find((c) => c.id === value);
        if (selectedClient) {
          setFormData((prev) => ({
            ...prev,
            clientId: value,
            clientName: `${selectedClient.firstName} ${selectedClient.lastName}`,
          }));
        }
      }

      if (errors[field]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.clientId) {
      newErrors.clientId = 'Danışan seçiniz';
    }
    if (!formData.appointmentDate) {
      newErrors.appointmentDate = 'Randevu tarihi gereklidir';
    }
    if (!formData.startTime) {
      newErrors.startTime = 'Başlangıç saati gereklidir';
    }
    if (!formData.endTime) {
      newErrors.endTime = 'Bitiş saati gereklidir';
    }
    if (!formData.type) {
      newErrors.type = 'Randevu türü seçiniz';
    }
    if (!formData.status) {
      newErrors.status = 'Durum seçiniz';
    }
    if (!formData.location) {
      newErrors.location = 'Konum seçiniz';
    }

    if (formData.startTime && formData.endTime) {
      const start = new Date(`2000-01-01T${formData.startTime}`);
      const end = new Date(`2000-01-01T${formData.endTime}`);
      if (start >= end) {
        newErrors.endTime = 'Bitiş saati başlangıç saatinden sonra olmalıdır';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (validateForm()) {
      await onSave(formData);
      resetForm();
      onClose();
    }
  };

  const resetForm = () => {
    setFormData({
      dietitianId: dietitianId,
      clientId: '',
      clientName: '',
      appointmentDate: '',
      startTime: '',
      endTime: '',
      type: '',
      status: 'Planlandı',
      notes: '',
      location: '',
      fee: 0,
      isPaid: false,
    });
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 2,
          pt: 3,
          px: 3,
          background: 'linear-gradient(135deg, #43A047 0%, #2E7D32 100%)',
          color: 'white',
        }}
      >
        <Typography variant="h5" component="div" fontWeight={700}>
          {editingAppointment ? 'Randevu Düzenle' : 'Yeni Randevu Ekle'}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ bgcolor: '#fafbfc', p: 2, mt: 1 }}>
        <Box sx={{ display: 'grid', gap: 2.5 }}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              bgcolor: 'white',
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              mb={2}
              sx={{
                pb: 1.5,
                borderBottom: `2px solid ${colors.primary.light}`,
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  bgcolor: colors.primary.light,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <PersonIcon sx={{ fontSize: 20, color: colors.primary.main }} />
              </Box>
              <Typography variant="h6" fontWeight={600}>
                Danışan ve Zaman
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              }}
            >
              <FormControl fullWidth required error={!!errors.clientId}>
                <InputLabel>Danışan</InputLabel>
                <Select
                  value={formData.clientId}
                  onChange={handleInputChange('clientId')}
                  label="Danışan"
                >
                  {clients.map((client) => (
                    <MenuItem key={client.id} value={client.id}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <PersonIcon
                          sx={{
                            fontSize: 18,
                            color: colors.text.secondary,
                          }}
                        />
                        {client.firstName} {client.lastName}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {errors.clientId && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, ml: 1.5 }}
                  >
                    {errors.clientId}
                  </Typography>
                )}
              </FormControl>

              <TextField
                fullWidth
                label="Randevu Tarihi"
                type="date"
                value={formData.appointmentDate}
                onChange={handleInputChange('appointmentDate')}
                error={!!errors.appointmentDate}
                helperText={errors.appointmentDate}
                required
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarIcon
                        sx={{
                          fontSize: 18,
                          color: colors.text.secondary,
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Başlangıç Saati"
                type="time"
                value={formData.startTime}
                onChange={handleInputChange('startTime')}
                error={!!errors.startTime}
                helperText={errors.startTime}
                required
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccessTimeIcon
                        sx={{
                          fontSize: 18,
                          color: colors.text.secondary,
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Bitiş Saati"
                type="time"
                value={formData.endTime}
                onChange={handleInputChange('endTime')}
                error={!!errors.endTime}
                helperText={errors.endTime}
                required
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccessTimeIcon
                        sx={{
                          fontSize: 18,
                          color: colors.text.secondary,
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
              />

              <FormControl fullWidth required error={!!errors.type}>
                <InputLabel>Randevu Türü</InputLabel>
                <Select
                  value={formData.type}
                  onChange={handleInputChange('type')}
                  label="Randevu Türü"
                >
                  <MenuItem value="Konsültasyon">Konsültasyon</MenuItem>
                  <MenuItem value="Takip">Takip</MenuItem>
                  <MenuItem value="Beslenme Programı">
                    Beslenme Programı
                  </MenuItem>
                  <MenuItem value="Grup Eğitimi">Grup Eğitimi</MenuItem>
                  <MenuItem value="Acil Konsültasyon">
                    Acil Konsültasyon
                  </MenuItem>
                  <MenuItem value="Çocuk Beslenme">Çocuk Beslenme</MenuItem>
                  <MenuItem value="Sporcu Beslenmesi">
                    Sporcu Beslenmesi
                  </MenuItem>
                  <MenuItem value="Hamilelik Beslenmesi">
                    Hamilelik Beslenmesi
                  </MenuItem>
                </Select>
                {errors.type && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, ml: 1.5 }}
                  >
                    {errors.type}
                  </Typography>
                )}
              </FormControl>

              <FormControl fullWidth required error={!!errors.status}>
                <InputLabel>Durum</InputLabel>
                <Select
                  value={formData.status}
                  onChange={handleInputChange('status')}
                  label="Durum"
                >
                  <MenuItem value="Planlandı">Planlandı</MenuItem>
                  <MenuItem value="Tamamlandı">Tamamlandı</MenuItem>
                  <MenuItem value="İptal Edildi">İptal Edildi</MenuItem>
                  <MenuItem value="Ertelendi">Ertelendi</MenuItem>
                </Select>
                {errors.status && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, ml: 1.5 }}
                  >
                    {errors.status}
                  </Typography>
                )}
              </FormControl>

              <FormControl fullWidth required error={!!errors.location}>
                <InputLabel>Konum</InputLabel>
                <Select
                  value={formData.location}
                  onChange={handleInputChange('location')}
                  label="Konum"
                >
                  <MenuItem value="Ofis">
                    <Box display="flex" alignItems="center" gap={1}>
                      <LocationIcon
                        sx={{
                          fontSize: 18,
                          color: colors.text.secondary,
                        }}
                      />
                      Ofis
                    </Box>
                  </MenuItem>
                  <MenuItem value="Online">
                    <Box display="flex" alignItems="center" gap={1}>
                      <LocationIcon
                        sx={{
                          fontSize: 18,
                          color: colors.text.secondary,
                        }}
                      />
                      Online
                    </Box>
                  </MenuItem>
                  <MenuItem value="Ev Ziyareti">
                    <Box display="flex" alignItems="center" gap={1}>
                      <LocationIcon
                        sx={{
                          fontSize: 18,
                          color: colors.text.secondary,
                        }}
                      />
                      Ev Ziyareti
                    </Box>
                  </MenuItem>
                </Select>
                {errors.location && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, ml: 1.5 }}
                  >
                    {errors.location}
                  </Typography>
                )}
              </FormControl>
            </Box>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: `1px solid ${colors.border?.light || '#e0e0e0'}`,
              bgcolor: 'white',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              mb={2}
              sx={{
                pb: 1.5,
                borderBottom: `2px solid ${colors.primary.light}`,
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  bgcolor: colors.primary.light,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <PaymentIcon
                  sx={{ fontSize: 20, color: colors.primary.main }}
                />
              </Box>
              <Typography variant="h6" fontWeight={600}>
                Ödeme ve Notlar
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                alignItems: 'center',
                mb: 2,
              }}
            >
              <TextField
                fullWidth
                label="Ücret (₺)"
                type="number"
                value={formData.fee || ''}
                onChange={handleInputChange('fee')}
                inputProps={{ min: 0, step: 0.01 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PaymentIcon
                        sx={{
                          fontSize: 18,
                          color: colors.text.secondary,
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isPaid}
                    onChange={handleInputChange('isPaid')}
                    sx={{
                      color: colors.primary.main,
                      '&.Mui-checked': {
                        color: colors.primary.main,
                      },
                    }}
                  />
                }
                label="Ödeme Yapıldı"
                sx={{ ml: 1 }}
              />
            </Box>
            <TextField
              fullWidth
              label="Notlar"
              value={formData.notes}
              onChange={handleInputChange('notes')}
              multiline
              rows={3}
              placeholder="Randevu hakkında notlar..."
              InputProps={{
                startAdornment: (
                  <InputAdornment
                    position="start"
                    sx={{ alignSelf: 'flex-start', mt: 1 }}
                  >
                    <NotesIcon
                      sx={{
                        fontSize: 18,
                        color: colors.text.secondary,
                      }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Paper>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          pt: 2,
          bgcolor: '#fafbfc',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <CancelButton onClick={handleClose} />
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{
            bgcolor: colors.primary.main,
            color: 'white',
            textTransform: 'none',
            px: 4,
            py: 1,
            borderRadius: 1.5,
            boxShadow: '0 4px 12px rgba(67,160,71,0.3)',
            '&:hover': {
              bgcolor: colors.hover.primary,
              boxShadow: '0 6px 16px rgba(67,160,71,0.4)',
            },
          }}
        >
          {editingAppointment ? 'Güncelle' : 'Kaydet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AppointmentModal;
